import axios, { AxiosError } from "axios";
import { useQuery } from "react-query";

import { getAppConfigSetting } from "../utils/getAppConfigSetting";
import { CASH_BALANCE_API } from "./constants";
import queryKeys from "./queryKeys";

enum TransferMechanism {
  Ach = "ACH",
  Wire = "WIRE",
  Check = "CHECK",
  Journal = "CASH_JOURNAL",
  Fee = "FEE",
  Credit = "CREDIT",
  PositionJournal = "POSITION_JOURNAL",
  Rtp = "RTP",
}

interface CashBalancesAvailableTransferDetails {
  transferId: string;
  mechanism: TransferMechanism;
  fundsPostedDate: string;
  totalCreditAmount: number;
}

interface CashBalancesAvailableAuthorizationDetails {
  id: number;
  externalId: string;
  account: string;
  amount: number;
  mechanism: TransferMechanism;
  state: "Authorized" | "Captured" | "Refunded";
  requested: string;
  lastUpdated: string;
}

interface CashBalancesDetails {
  amountAvailableToWithdraw: number;
  unadjustedAmountAvailableToWithdraw?: number;
  totalEquity?: number;
  netLiquidity?: number;
  pendingDebitInterest?: number;
  pendingDebitDividends?: number;
  fullyPaidUnsettledFunds?: number;
  unsettledFunds?: {
    fullyPaid: number;
    notFullyPaid: number;
  };
  openAuthorizations?: CashBalancesAvailableAuthorizationDetails[];
  intradayDeposits?: CashBalancesAvailableTransferDetails[];
  intradayWithdrawals?: CashBalancesAvailableTransferDetails[];
  amountOfRecentDeposits?: number;
  recentWithheldDeposits?: CashBalancesAvailableTransferDetails[];
  recentDepositThreshold?: number;
  includeFullyPaidUnsettledFunds?: boolean;
  intradayReturns?: CashBalancesAvailableTransferDetails[];
}

export interface CashBalancesResponse {
  account?: string;
  openBalance?: number;
  balanceDetails: CashBalancesDetails;
}

export function useGetCashBalances(account: string) {
  const apiURL = `${getAppConfigSetting("apiUrl")}${CASH_BALANCE_API}`;

  const fetchCashBalance = async () => {
    const { data } = await axios.get<CashBalancesResponse>(
      `${apiURL}/${account}`,
      {
        withCredentials: true,
      }
    );
    return data;
  };

  return useQuery<CashBalancesResponse, AxiosError>(
    [queryKeys.getCashBalances, account],
    fetchCashBalance,
    {
      enabled: !!account,
    }
  );
}
